import { useState, useEffect } from "react";
import {
  processUnlocodeFile,
  processImoFile,
  processSubregionFile,
  downloadFacilityCodes,
  downloadGlnTable,
  getUserGlnRecords,
  getUserPermissions,
  getUserProfile,
  addGlnRecord,
  getGlnRecordById,
  updateGlnRecord,
  deactivateGlnRecord,
  searchGlnRecords,
  validateGlnCode,
  bulkAddGlnRecords,
  bulkUpdateGlnRecords,
  bulkDeactivateGlnRecords,
  exportUserGlnRecords,
  fetchCountryData
} from "../model/GLNModel";
import unlocodeFile from "../../assets/unlocode.xlsx";
import imoFile from "../../assets/IMO_sample.xlsx";
import subregionFile from "../../assets/subregion_sample.xlsx";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";

// Role types mapping
const ROLE_TYPES = [
  'Seaport',
  'Rail Terminal',
  'Road Terminal',
  'Airport',
  'Postal Exchange',
  'Intermodal Terminal',
  'Fixed Transport Function',
  'Border Crossing Function'
];

// Convert role type name to array format
const convertRoleTypeToArray = (roleType) => {
  if (!roleType) {
    return '0,0,0,0,0,0,0,0';
  }
  
  const roleTypeArray = new Array(8).fill('0');
  const roleTypeIndex = ROLE_TYPES.indexOf(roleType);
  
  if (roleTypeIndex !== -1) {
    roleTypeArray[roleTypeIndex] = '1';
  }
  
  return roleTypeArray.join(',');
};

// Convert array format back to role type name
const convertArrayToRoleType = (roleTypeArray) => {
  if (!roleTypeArray || roleTypeArray === '0,0,0,0,0,0,0,0') {
    return '';
  }
  
  const parts = roleTypeArray.split(',');
  const index = parts.findIndex(part => part === '1');
  
  if (index !== -1 && index < ROLE_TYPES.length) {
    return ROLE_TYPES[index];
  }
  
  return '';
};

const MyGLNsVM = () => {
  const { handleToastOpen } = useToast();

  // File Upload State
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadType, setUploadType] = useState("SUBREGION");
  const [selectedCountry, setSelectedCountry] = useState("All");
  const [hasUploadedSubregion, setHasUploadedSubregion] = useState(false);
  const [countries, setCountries] = useState([]);

  // Record Management State
  const [userProfile, setUserProfile] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [glnRecords, setGlnRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(15);
  const [recordsByCategory, setRecordsByCategory] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);

  // Search and Filter State
  const [selectedCategory, setSelectedCategory] = useState("myCompanyRecords");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRecords, setSelectedRecords] = useState([]);

  // Sorting State
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  // Form State
  const [formData, setFormData] = useState({
    gln_type: '',
    code_type: '',
    generated_code: '',
    role_type: '',
    latitude: '',
    longitude: '',
    gln_status: 'active',
    data_source: 'manual',
    facility_name: '',
    country: '',
  });

  // Initialize
  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    const subregionUploaded = localStorage.getItem("subregionUploaded");
    if (subregionUploaded === "true") {
      setHasUploadedSubregion(true);
    }
    initializeData();
  }, []);

  useEffect(() => {
    filterAndSortRecords();
  }, [selectedCategory, searchQuery, glnRecords, sortBy, sortDirection]);

  useEffect(() => {
    if (userProfile) {
      fetchGlnRecords();
    }
  }, [userProfile]);

  // Get User Email
  const getUserEmail = () => {
    const email = sessionStorage.getItem("email") || sessionStorage.getItem("userEmail");
    return email;
  };

  // Permission Storage
  const savePermissionsToLocalStorage = (permissionsArray) => {
    try {
      if (!permissionsArray || permissionsArray.length === 0) {
        console.warn("⚠️ No permissions to save");
        localStorage.removeItem("permissionData");
        localStorage.removeItem("userPermissions");
        return;
      }

      const featureMap = {
        "view": "feature_gln_view",
        "create": "feature_gln_create",
        "edit": "feature_gln_edit",
        "deactivate": "feature_gln_deactivate",
        "download": "feature_gln_download"
      };

      const permissionFeatures = permissionsArray
        .map(perm => featureMap[perm.permission_type])
        .filter(feature => feature);

      const permissionString = permissionFeatures.join(',');

      localStorage.setItem("permissionData", permissionString);
      localStorage.setItem("userPermissions", JSON.stringify(permissionsArray));

      console.log("✅ Permissions saved to localStorage:", {
        permissionString,
        permissionsArray
      });
    } catch (error) {
      console.error("❌ Error saving permissions to localStorage:", error);
    }
  };

  const loadPermissionsFromLocalStorage = () => {
    try {
      const permissionString = localStorage.getItem("permissionData");
      const permissionsJson = localStorage.getItem("userPermissions");

      if (permissionsJson) {
        const parsedPermissions = JSON.parse(permissionsJson);
        console.log("✅ Loaded permissions from localStorage:", parsedPermissions);
        return parsedPermissions;
      }

      if (permissionString) {
        console.log("✅ Found permission string in localStorage:", permissionString);
        return [];
      }

      console.warn("⚠️ No permissions found in localStorage");
      return [];
    } catch (error) {
      console.error("❌ Error loading permissions from localStorage:", error);
      return [];
    }
  };

  const initializeData = async () => {
    const email = getUserEmail();
    
    if (!email) {
      console.error("❌ No email found in sessionStorage");
      handleToastOpen("error", "User email not found. Please log in again.");
      return;
    }

    setIsLoading(true);
    
    try {
      const cachedPermissions = loadPermissionsFromLocalStorage();
      if (cachedPermissions.length > 0) {
        setPermissions(cachedPermissions);
        console.log("✅ Using cached permissions:", cachedPermissions);
      }

      const profileResponse = await getUserProfile(email);
      const permissionsResponse = await getUserPermissions(email);

      setUserProfile(profileResponse.data);
      
      const fetchedPermissions = permissionsResponse.data || [];
      setPermissions(fetchedPermissions);
      savePermissionsToLocalStorage(fetchedPermissions);

      console.log("✅ User profile loaded:", profileResponse.data);
      console.log("✅ Permissions loaded:", fetchedPermissions);

      await fetchGlnRecords();

    } catch (error) {
      console.error("❌ Error loading data:", error);
      handleToastOpen("error", `Failed to load data: ${error.message}`);
      
      const cachedPermissions = loadPermissionsFromLocalStorage();
      if (cachedPermissions.length > 0) {
        setPermissions(cachedPermissions);
        console.log("✅ Using cached permissions after error:", cachedPermissions);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch GLN Records
  const fetchGlnRecords = async () => {
    const email = getUserEmail();
    if (!email) return;

    try {
      setIsLoading(true);
      const response = await getUserGlnRecords(email, 1, 10000);
      
      if (response.success) {
        const recordsData = response.data;
        setGlnRecords(recordsData.records || []);
        setTotalRecords(recordsData.totalRecords || 0);
        setRecordsByCategory(recordsData.recordsByCategory || {});
        setLastUpdated(recordsData.lastUpdated);
      }
    } catch (error) {
      console.error("Error fetching GLN records:", error);
      handleToastOpen("error", "Failed to fetch GLN records");
    } finally {
      setIsLoading(false);
    }
  };

  // Add Record
  const handleAddRecord = async (recordData) => {
    const email = getUserEmail();
    
    try {
      setIsLoading(true);
      const response = await addGlnRecord({
        ...recordData,
        created_by: email,
        data_provider: userProfile?.org_id,
      });
      
      if (response.success) {
        handleToastOpen("success", "GLN record added successfully");
        await fetchGlnRecords();
        resetFormData();
        return response;
      } else {
        throw new Error(response.message || "Failed to add record");
      }
    } catch (error) {
      console.error("Error adding GLN record:", error);
      handleToastOpen("error", error.message || "Failed to add GLN record");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Edit Record
const handleEditRecord = async (recordId, recordData) => {
  const email = getUserEmail();
  
  try {
    setIsLoading(true);
    console.log("🚀 Starting handleEditRecord");
    console.log("📋 Original recordData:", recordData);
    
    // Convert role_type to array format if it's a name
    let roleTypeArray = recordData.role_type;
    if (roleTypeArray && !roleTypeArray.includes(',')) {
      roleTypeArray = convertRoleTypeToArray(roleTypeArray);
      console.log("🔄 Converted role_type from name to array:", roleTypeArray);
    }
    
    // CHANGED: Don't filter out facility_name anymore
    const { country, ...validFields } = recordData;
    
    const response = await updateGlnRecord(recordId, {
      ...validFields,
      role_type: roleTypeArray,
      updated_by: email,
    });
    
    console.log("📋 Got response from updateGlnRecord:", response);
    
    if (response.success) {
      console.log("✅ Update successful, showing success toast");
      handleToastOpen("success", response.message || "GLN record updated successfully");
      
      try {
        console.log("🔄 Fetching GLN records...");
        await fetchGlnRecords();
        console.log("✅ Successfully fetched GLN records");
      } catch (fetchError) {
        console.error("❌ Error fetching records after update:", fetchError);
      }
      
      console.log("✅ handleEditRecord completed successfully");
      return response;
    } else {
      console.log("❌ Response success is false, throwing error");
      throw new Error(response.message || "Failed to update record");
    }
  } catch (error) {
    console.error("❌ MAIN CATCH in handleEditRecord:", error);
    handleToastOpen("error", error.message || "Failed to update GLN record");
    throw error;
  } finally {
    console.log("🏁 Setting loading to false");
    setIsLoading(false);
  }
};

  // Deactivate Record
  const handleDeactivateRecord = async (recordId) => {
    try {
      setIsLoading(true);
      const response = await deactivateGlnRecord(recordId);
      if (response.success) {
        handleToastOpen("success", "GLN record deactivated successfully");
        await fetchGlnRecords();
        setSelectedRecords(prev => prev.filter(id => id !== recordId));
        return response;
      } else {
        throw new Error(response.message || "Failed to deactivate record");
      }
    } catch (error) {
      console.error("Error deactivating GLN record:", error);
      handleToastOpen("error", error.message || "Failed to deactivate GLN record");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Get Record
  const handleGetRecord = async (recordId) => {
    try {
      setIsLoading(true);
      const response = await getGlnRecordById(recordId);
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to get record");
      }
    } catch (error) {
      console.error("Error getting GLN record:", error);
      handleToastOpen("error", error.message || "Failed to get GLN record");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Validate Code
  const handleValidateCode = async (codeData) => {
    try {
      const response = await validateGlnCode(codeData);
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to validate code");
      }
    } catch (error) {
      console.error("Error validating GLN code:", error);
      throw error;
    }
  };

  // Export Records
  const handleExportRecords = async (records, category, isSingleRecord = false) => {
    try {
      setIsLoading(true);
      
      const email = getUserEmail();
      if (!email) {
        throw new Error("User email not found");
      }

      let exportParams = {
        email: email,
        category: category,
        searchQuery: searchQuery,
        sortBy: sortBy,
        sortDirection: sortDirection,
      };

      if (isSingleRecord && Array.isArray(records) && records.length === 1) {
        exportParams.recordIds = records[0];
        exportParams.exportType = 'single';
      } 
      else if (selectedRecords.length > 0) {
        exportParams.recordIds = selectedRecords.join(',');
        exportParams.exportType = 'selected';
      }
      else {
        exportParams.exportType = 'filtered';
      }

      const response = await exportUserGlnRecords(email, exportParams);
      
      if (response.success) {
        const recordCount = isSingleRecord ? 1 : 
                          selectedRecords.length > 0 ? selectedRecords.length : 
                          filteredRecords.length;
        handleToastOpen("success", `Successfully exported ${recordCount} GLN record${recordCount !== 1 ? 's' : ''}`);
      } else {
        throw new Error(response.message || "Failed to export records");
      }
    } catch (error) {
      console.error("Error exporting GLN records:", error);
      handleToastOpen("error", error.message || "Failed to export GLN records");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Bulk Add
  const handleBulkAdd = async (records) => {
    const email = getUserEmail();
    
    try {
      setIsLoading(true);
      const recordsWithMetadata = records.map(record => ({
        ...record,
        created_by: email,
        data_provider: userProfile?.org_id,
      }));
      
      const response = await bulkAddGlnRecords(recordsWithMetadata);
      
      if (response.success) {
        handleToastOpen("success", 
          `Bulk add completed: ${response.data.successful} successful, ${response.data.failed} failed`
        );
        await fetchGlnRecords();
        return response;
      } else {
        throw new Error(response.message || "Failed to bulk add records");
      }
    } catch (error) {
      console.error("Error bulk adding records:", error);
      handleToastOpen("error", error.message || "Failed to bulk add records");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Bulk Edit
  const handleBulkEdit = async (updates) => {
    const email = getUserEmail();
    
    try {
      setIsLoading(true);
      const updatesWithMetadata = updates.map(update => ({
        ...update,
        data: {
          ...update.data,
          updated_by: email,
        }
      }));
      
      const response = await bulkUpdateGlnRecords(updatesWithMetadata);
      
      if (response.success) {
        handleToastOpen("success", 
          `Bulk update completed: ${response.data.successful} successful, ${response.data.failed} failed`
        );
        await fetchGlnRecords();
        setSelectedRecords([]);
        return response;
      } else {
        throw new Error(response.message || "Failed to bulk update records");
      }
    } catch (error) {
      console.error("Error bulk updating records:", error);
      handleToastOpen("error", error.message || "Failed to bulk update records");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Bulk Deactivate
  const handleBulkDeactivate = async (recordIds = selectedRecords) => {
    if (recordIds.length === 0) {
      handleToastOpen("warning", "No records selected for deactivation");
      return;
    }

    try {
      setIsLoading(true);
      const response = await bulkDeactivateGlnRecords(recordIds);
      
      if (response.success) {
        handleToastOpen("success", 
          `Bulk deactivate completed: ${response.data.successful} successful, ${response.data.failed} failed`
        );
        await fetchGlnRecords();
        setSelectedRecords([]);
        return response;
      } else {
        throw new Error(response.message || "Failed to bulk deactivate records");
      }
    } catch (error) {
      console.error("Error bulk deactivating records:", error);
      handleToastOpen("error", error.message || "Failed to bulk deactivate records");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Sorting
  const handleSortChange = (property) => {
    const isAsc = sortBy === property && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const sortRecords = (records) => {
    if (!sortBy) return records;

    return [...records].sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  // Filter and Sort Records
  const filterAndSortRecords = () => {
    let filtered = [...glnRecords];

    switch (selectedCategory) {
      case "myCompanyRecords":
        filtered = filtered.filter(record => {
          return String(record.data_provider) === String(userProfile?.org_id);
        });
        break;
      case "createdByMe":
        filtered = filtered.filter(record => record.created_by_user);
        break;
      case "bicFacilities":
        filtered = filtered.filter(record => record.code_type === "BIC");
        break;
      case "unlocodePorts":
        filtered = filtered.filter(record => record.code_type === "UNLOCODE");
        break;
      case "others":
        filtered = filtered.filter(record => 
          !['BIC', 'UNLOCODE'].includes(record.code_type)
        );
        break;
      default:
        break;
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(record =>
        String(record.gln_id).toLowerCase().includes(query) ||
        String(record.generated_code || "").toLowerCase().includes(query) ||
        String(record.role_type || "").toLowerCase().includes(query) ||
        String(record.role_type_name || "").toLowerCase().includes(query) ||
        String(record.created_by_name || "").toLowerCase().includes(query) ||
        String(record.facility_name || "").toLowerCase().includes(query) ||
        String(record.country_name || "").toLowerCase().includes(query)
      );
    }

    const sorted = sortRecords(filtered);
    setFilteredRecords(sorted);
  };

  // Category Change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedRecords([]);
    setCurrentPage(1);
  };

  // Search Change
  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  // Record Selection
  const handleRecordSelection = (recordId, isSelected) => {
    if (isSelected) {
      setSelectedRecords(prev => [...prev, recordId]);
    } else {
      setSelectedRecords(prev => prev.filter(id => id !== recordId));
    }
  };

  // Bulk Selection
  const handleBulkSelection = (isSelectAll) => {
    if (isSelectAll) {
      setSelectedRecords(filteredRecords.map(record => record.gln_id));
    } else {
      setSelectedRecords([]);
    }
  };

  // Form Change
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Reset Form
  const resetFormData = () => {
    setFormData({
      gln_type: '',
      code_type: '',
      generated_code: '',
      role_type: '',
      latitude: '',
      longitude: '',
      gln_status: 'active',
      data_source: 'manual',
      facility_name: '',
      country: '',
    });
  };

  // Load Record to Form
  const loadRecordToForm = (record) => {
    let glnTypeValue = record.gln_type || '';
    if (glnTypeValue) {
      glnTypeValue = glnTypeValue.toLowerCase().replace(/ /g, '_');
    }

    let roleTypeValue = '';
    if (record.role_type_name) {
      roleTypeValue = record.role_type_name;
    } else if (record.role_type && record.role_type.includes(',')) {
      roleTypeValue = convertArrayToRoleType(record.role_type);
    } else if (record.role_type) {
      roleTypeValue = record.role_type;
    }

    console.log("🔄 Loading record to form:", {
      original_role_type: record.role_type,
      role_type_name: record.role_type_name,
      converted_role_type: roleTypeValue
    });

    setFormData({
      gln_type: glnTypeValue,
      code_type: record.code_type || '',
      generated_code: record.generated_code || '',
      role_type: roleTypeValue,
      latitude: record.latitude ? parseFloat(record.latitude).toFixed(6) : '',
      longitude: record.longitude ? parseFloat(record.longitude).toFixed(6) : '',
      gln_status: record.gln_status ? record.gln_status.toLowerCase() : 'active',
      data_source: record.data_source || 'manual',
      facility_name: record.facility_name && record.facility_name !== 'Unknown' ? record.facility_name : '',
      country: record.country_code || '',
    });
  };

  // Pagination
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRecordsPerPageChange = (newValue) => {
    setRecordsPerPage(newValue);
    setCurrentPage(1);
  };

  const getPaginatedRecords = () => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return filteredRecords.slice(startIndex, endIndex);
  };

  // File Upload
  const handleFileChoose = (files) => {
    const file = files[0];
    setSelectedFile(file);
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      handleToastOpen("error", "Please select a file first");
      return;
    }

    if (uploadType === "UNLOCODE" && !hasUploadedSubregion) {
      handleToastOpen(
        "error",
        "You must upload SUBREGION data first before uploading UNLOCODE files"
      );
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("fileType", uploadType);

      let response;
      if (uploadType === "UNLOCODE") {
        response = await processUnlocodeFile(formData);
        handleToastOpen(
          "success",
          `UNLOCODE file processed successfully: ${
            response.newRecordsProcessed
          } new records, ${response.recordsUpdated} updated records, ${
            response.recordsOverwritten || 0
          } overwritten records`
        );
      } else if (uploadType === "IMO") {
        response = await processImoFile(formData);
        if (response && response.stats) {
          const stats = response.stats;
          handleToastOpen(
            "success",
            `IMO file processed successfully: ${
              stats.facility_table.inserted_records
            } new records, ${
              stats.facility_table.updated_records
            } updated records, ${stats.errors || 0} errors`
          );
        } else {
          handleToastOpen("success", `IMO file processed successfully`);
        }
      } else if (uploadType === "SUBREGION") {
        response = await processSubregionFile(formData);
        if (response && response.stats) {
          const stats = response.stats;
          const totalRecordsProcessed =
            stats.region_table.new_records + stats.region_table.updated_records;

          if (totalRecordsProcessed > 0) {
            setHasUploadedSubregion(true);
            localStorage.setItem("subregionUploaded", "true");
          }

          handleToastOpen(
            "success",
            `Subregion file processed successfully: ${
              stats.region_table.new_records
            } new records, ${
              stats.region_table.updated_records
            } updated records, ${stats.error_count || 0} errors`
          );
        } else {
          handleToastOpen("success", `Subregion file processed successfully`);
          setHasUploadedSubregion(true);
          localStorage.setItem("subregionUploaded", "true");
        }
      }

      setSelectedFile(null);
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.message && errorData.message.includes("duplicate")) {
          handleToastOpen("error", "Duplicate records found in the file");
        } else if (errorData.message) {
          handleToastOpen("error", errorData.message);
        } else {
          handleToastOpen("error", `Error processing ${uploadType} file`);
        }
      } else {
        handleToastOpen(
          "error",
          error.message || `Error processing ${uploadType} file`
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSampleFileDownload = () => {
    const link = document.createElement("a");

    if (uploadType === "UNLOCODE") {
      link.href = unlocodeFile;
      link.download = "unlocode_sample.xlsx";
    } else if (uploadType === "IMO") {
      link.href = imoFile;
      link.download = "IMO_sample.xlsx";
    } else if (uploadType === "SUBREGION") {
      link.href = subregionFile;
      link.download = "subregion_sample.xlsx";
    }

    link.click();
  };

  const handleBicSmdgDownload = async () => {
    setIsLoading(true);
    try {
      await downloadFacilityCodes(selectedCountry);
      const message =
        selectedCountry === "All"
          ? "Facility codes for all countries exported successfully"
          : `Facility codes for ${selectedCountry} exported successfully`;
      handleToastOpen("success", message);
    } catch (error) {
      if (error.message && error.message.includes("timeout")) {
        handleToastOpen(
          "error",
          "The request took too long to complete. Please try again later."
        );
      } else {
        handleToastOpen(
          "error",
          "Unable to export facility codes. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGlnTableDownload = async () => {
    setIsLoading(true);
    try {
      await downloadGlnTable(selectedCountry);
      const message =
        selectedCountry === "All"
          ? "GLN table data for all countries exported successfully"
          : `GLN table data for ${selectedCountry} exported successfully`;
      handleToastOpen("success", message);
    } catch (error) {
      if (error.message && error.message.includes("timeout")) {
        handleToastOpen(
          "error",
          "The request took too long to complete. Please try again later."
        );
      } else {
        handleToastOpen(
          "error",
          "Unable to export GLN table data. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadTypeChange = (value) => {
    if (value === "UNLOCODE" && !hasUploadedSubregion) {
      handleToastOpen(
        "warning",
        "You must upload SUBREGION data first before uploading UNLOCODE files"
      );
    }
    setUploadType(value);
    setSelectedFile(null);
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
  };

  // Permissions
  const hasPermission = (permissionType) => {
    if (permissions && permissions.length > 0) {
      const hasPermInState = permissions.some(permission => 
        permission.permission_type === permissionType
      );
      return hasPermInState;
    }

    const permissionData = localStorage.getItem("permissionData") || "";
    const featureMap = {
      "view": "feature_gln_view",
      "create": "feature_gln_create", 
      "edit": "feature_gln_edit",
      "deactivate": "feature_gln_deactivate",
    };
    const requiredFeature = featureMap[permissionType];
    const hasPermInStorage = permissionData.includes(requiredFeature);
    
    return hasPermInStorage;
  };

  const hasBtnPermission = () => {
    if (permissions && permissions.length > 0) {
      return permissions.some(permission => 
        permission.permission_type === "download"
      );
    }

    const permissionData = localStorage.getItem("permissionData") || "";
    const hasDownloadPerm = permissionData.includes("feature_gln_download");
    return hasDownloadPerm;
  };

  const canEdit = () => hasPermission("edit");
  const canCreate = () => hasPermission("create");
  const canDeactivate = () => hasPermission("deactivate");
  const canView = () => hasPermission("view");

  // Format Helpers
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const formatLastUpdated = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return "Less than 1 hour ago";
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  // Fetch Countries
  const fetchCountries = async () => {
    try {
      const data = await fetchCountryData();
      
      const transformedData = data.map(country => ({
        country_code: country.countryCode,
        country_name: country.countryName
      }));
      
      setCountries(transformedData);
      console.log("✅ Countries loaded:", transformedData.length);
    } catch (error) {
      console.error("Error fetching countries:", error);
      handleToastOpen("error", "Failed to load countries");
      setCountries([]);
    }
  };

  // Return
  return {
    selectedFile,
    isLoading,
    uploadType,
    selectedCountry,
    hasUploadedSubregion,
    userProfile,
    permissions,
    glnRecords,
    filteredRecords,
    paginatedRecords: getPaginatedRecords(),
    totalRecords,
    currentPage,
    recordsPerPage,
    recordsByCategory,
    lastUpdated,
    selectedCategory,
    searchQuery,
    selectedRecords,
    formData,
    sortBy,
    sortDirection,
    handleFileChoose,
    handleUploadFile,
    handleSampleFileDownload,
    handleBicSmdgDownload,
    handleGlnTableDownload,
    handleUploadTypeChange,
    handleCountryChange,
    handleAddRecord,
    handleEditRecord,
    handleDeactivateRecord,
    handleGetRecord,
    handleValidateCode,
    handleBulkAdd,
    handleBulkEdit,
    handleBulkDeactivate,
    fetchGlnRecords,
    handleExportRecords,
    handleCategoryChange,
    handleSearchChange,
    handleRecordSelection,
    handleBulkSelection,
    handleSortChange,
    handleFormChange,
    resetFormData,
    loadRecordToForm,
    handlePageChange,
    handleRecordsPerPageChange,
    hasPermission,
    hasBtnPermission,
    canEdit,
    canCreate,
    canDeactivate,
    canView,
    formatDate,
    formatLastUpdated,
    isAllSelected: selectedRecords.length === filteredRecords.length && filteredRecords.length > 0,
    isSomeSelected: selectedRecords.length > 0 && selectedRecords.length < filteredRecords.length,
    hasSelectedRecords: selectedRecords.length > 0,
    countries,
    convertRoleTypeToArray,
    convertArrayToRoleType,
    ROLE_TYPES,
  };
};

export default MyGLNsVM;